import { getNotifications } from '@/api/communication/notification';
import { eventBus } from '@/utils/core/eventBus';
import { useTranslator } from '@/composables/useTranslator';
import { pushLegacyToast } from './legacyDialogs';
import { log } from '@/utils/core/vueLogger';

// Port of what the legacy notification component (notification.js, hosted by the removed nav.htm)
// did on its own timer besides rendering its list - the Vue panel list itself is legacyNavState.js
// refreshNotifications(), fed from the same cached getNotifications() this poll force-refreshes:
//   - the regular geolocation refresh (evtGeolocUpdated, used by the legacy map),
//   - new unread notifications: score-claim confetti (PointsDialogZone) and the browser push,
//   - the "offline for too long" warning.
// Unlike the legacy loop, a skipped tick (hidden tab, offline, BO hidden app) re-arms the timer
// instead of stopping the poll for good.

const t = useTranslator();

const POLL_DELAYS_MS = { '': 10000, DEV: 30000 };
const DEFAULT_POLL_MS = 5 * 60000;
const PUSH_SUMMARY_THRESHOLD = 3;
const PUSH_ICON = '/m.eagle/img/logo-transparent-unmarged-2026.png';
const PUSH_TITLE = 'eCOS Notification';
// RG #22908: score claim validation notifications carry urlData = "{status};{pointsValidated}".
const SCORE_CLAIM_ALERTS = new Set(['ALERT_SHORT_69', 'ALERT_SHORT_71']);

export const pollDelayFor = (env) => POLL_DELAYS_MS[env ?? ''] ?? DEFAULT_POLL_MS;

export function findNewNotifications(previousIds, notifications) {
	const unread = (notifications ?? []).filter((n) => n.state === 0);
	return { unreadIds: new Set(unread.map((n) => n.id)), fresh: unread.filter((n) => !previousIds.has(n.id)) };
}

export function scoreClaimEvents(notifications) {
	return (notifications ?? [])
		.filter((n) => n.type === 3 && SCORE_CLAIM_ALERTS.has(n.alertName))
		.map((n) => {
			const [status, points] = String(n.url ?? '')
				.split(';')
				.map((part) => parseInt(part, 10));
			return Number.isNaN(status) || Number.isNaN(points) ? null : { status, pointsValidated: points, notificationId: n.id };
		})
		.filter(Boolean);
}

export const formatOfflineDuration = (minutes) => (minutes > 60 ? `${parseInt(minutes / 60, 10)}H` : `${parseInt(minutes, 10)}min`);

let win = null;
let context = () => null;
let onFetched = () => {};
let timer = null;
let previousIds = new Set();

const safe = (fn, fallback = null) => {
	try {
		const value = fn();
		return value === undefined ? fallback : value;
	} catch {
		return fallback;
	}
};

function refreshGeoloc() {
	const sfaHelper = safe(() => (win.require.defined('sfaHelper') ? win.require('sfaHelper') : null));
	if (!safe(() => sfaHelper.isGeolocActivated(), false)) return;
	safe(() => win.geolocHelper.init(() => win.amplify.publish('evtGeolocUpdated')));
}

// notification.js refreshUI(): externis.minutesAllowedOffline (24h) after the last logon, once per document.
function checkOfflineDuration(online) {
	const ext = win.externis;
	const ctx = context();
	if (online || safe(() => ext.offlineWarning, false) || !safe(() => ext.ecosVM.currentView())) return;

	const lastLogonDate = safe(() => ctx.user.lastLogonDate);
	const minutesOffline = safe(() => win.dateHelper.getDiffMinutes(new Date(lastLogonDate), new Date()), 0);
	if (!lastLogonDate || !(minutesOffline >= safe(() => ext.minutesAllowedOffline, 1440))) return;

	ext.offlineWarning = true;
	pushLegacyToast(`${t('OfflineWarning')} - ${t('OfflineWarningMsg', [formatOfflineDuration(minutesOffline)])}`, 'warning');
}

function showBrowserNotification(id, body) {
	safe(() =>
		win.navigator.serviceWorker
			.getRegistration()
			.then((reg) =>
				reg.showNotification(PUSH_TITLE, {
					body,
					badge: PUSH_ICON,
					icon: PUSH_ICON,
					data: win.location.hostname,
					tag: id,
					actions: [
						{ action: 'open', title: 'Open' },
						{ action: 'close', title: 'Close' },
					],
				})
			)
			.catch((error) => log('warn', 'legacyNotifications', `Service Worker registration error: ${error.message}`))
	);
}

function pushBrowserNotifications(fresh) {
	const NotificationApi = safe(() => win.Notification);
	if (typeof NotificationApi !== 'function') return;

	Promise.resolve(safe(() => NotificationApi.requestPermission()))
		.then((permission) => {
			if (permission !== 'granted') return;
			if (fresh.length > PUSH_SUMMARY_THRESHOLD) showBrowserNotification(0, t('YouHaveNewMsg', [fresh.length]));
			else fresh.forEach((n) => showBrowserNotification(n.id, n.title));
		})
		.catch((error) => log('warn', 'legacyNotifications', `Notification permission failed: ${error?.message}`));
}

function handleNotifications(notifications) {
	const { unreadIds, fresh } = findNewNotifications(previousIds, notifications);
	previousIds = unreadIds;
	safe(() => onFetched());
	if (!fresh.length) return;

	scoreClaimEvents(fresh).forEach((event) => eventBus.emit('score-claim-validated', event));
	if (!safe(() => win.externis.hiddenApp, false)) pushBrowserNotifications(fresh);
}

async function tick() {
	clearTimeout(timer);
	timer = null;
	if (!win) return;
	const current = win;

	try {
		const online = safe(() => context().isOnline(), true) !== false;
		checkOfflineDuration(online);

		if (online && safe(() => current.document.visibilityState) === 'visible' && !safe(() => current.externis.hiddenApp, false)) {
			refreshGeoloc();
			const notifications = await getNotifications(true);
			if (win === current) handleNotifications(notifications);
		}
	} catch (error) {
		log('warn', 'legacyNotifications', `poll failed: ${error.message}`);
	} finally {
		if (win === current) timer = setTimeout(tick, pollDelayFor(safe(() => current.externis.env, '')));
	}
}

// evtSynchroEnd (SALFO): the legacy component started - and restarted - its poll there.
export const pollLegacyNotificationsNow = () => tick();

export function attachLegacyNotificationPoller(iframeWindow, { getContext, onRefreshed } = {}) {
	detachLegacyNotificationPoller();
	win = iframeWindow ?? null;
	context = getContext ?? (() => null);
	onFetched = onRefreshed ?? (() => {});
}

export function detachLegacyNotificationPoller() {
	clearTimeout(timer);
	timer = null;
	win = null;
	context = () => null;
	onFetched = () => {};
	previousIds = new Set();
}
